import { AlertCircle, CheckCircle2, Info, X } from 'lucide-react';
import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import { cn } from '@/lib/utils';

type Variant = 'success' | 'error' | 'info';
type Toast = { id: number; variant: Variant; message: string };

type ToastContextValue = {
  toast: (message: string, variant?: Variant) => void;
  success: (message: string) => void;
  error: (message: string) => void;
};

const ToastContext = createContext<ToastContextValue | undefined>(undefined);

const AUTO_DISMISS_MS = 5000;

const ICON = { success: CheckCircle2, error: AlertCircle, info: Info } as const;

const STYLE: Record<Variant, string> = {
  success: 'border-[var(--success)]/40 bg-card text-foreground',
  error: 'border-destructive/50 bg-card text-foreground',
  info: 'border-border bg-card text-foreground',
};

const ICON_COLOR: Record<Variant, string> = {
  success: 'text-[var(--success)]',
  error: 'text-destructive',
  info: 'text-muted-foreground',
};

/** Powiadomienia = toasty w prawym dolnym rogu (konwencja UX BillVis). */
export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const dismiss = useCallback((id: number) => setToasts((t) => t.filter((x) => x.id !== id)), []);

  const toast = useCallback(
    (message: string, variant: Variant = 'info') => {
      // licznik z czasu + losowości nie jest potrzebny — wystarczy monotoniczny id z stanu
      setToasts((prev) => {
        const id = (prev[prev.length - 1]?.id ?? 0) + 1;
        setTimeout(() => dismiss(id), AUTO_DISMISS_MS);
        return [...prev, { id, variant, message }];
      });
    },
    [dismiss],
  );

  const value = useMemo<ToastContextValue>(
    () => ({
      toast,
      success: (m: string) => toast(m, 'success'),
      error: (m: string) => toast(m, 'error'),
    }),
    [toast],
  );

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div
        aria-live="polite"
        aria-atomic="false"
        className="pointer-events-none fixed bottom-4 right-4 z-[100] flex w-[min(24rem,calc(100vw-2rem))] flex-col gap-2"
      >
        {toasts.map((t) => {
          const Icon = ICON[t.variant];
          return (
            <div
              key={t.id}
              role={t.variant === 'error' ? 'alert' : 'status'}
              className={cn(
                'pointer-events-auto flex items-start gap-2.5 rounded-lg border p-3 shadow-lg',
                STYLE[t.variant],
              )}
            >
              <Icon className={cn('mt-0.5 size-4 shrink-0', ICON_COLOR[t.variant])} />
              <span className="min-w-0 flex-1 break-words text-sm">{t.message}</span>
              <button
                type="button"
                onClick={() => dismiss(t.id)}
                aria-label="Zamknij"
                className="shrink-0 rounded p-0.5 text-muted-foreground transition-colors hover:text-foreground"
              >
                <X className="size-3.5" />
              </button>
            </div>
          );
        })}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast musi być wewnątrz ToastProvider');
  return ctx;
}
